import { Sidebar } from "@/components/Sidebar";
import { useState, useEffect, useCallback, useRef } from "react";
import { api, type Product, type SidebarCategory } from "@/lib/api";
import { Loader, Drawer, Pagination, Modal } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconChevronDown, IconX, IconZoomIn, IconZoomOut, IconArrowsMaximize } from "@tabler/icons-react";
import { useSearchParams } from "react-router";
import { Footer } from "@/widgets/Footer";
import { useApplicationForm } from "@/lib/ApplicationFormContext";
import { Button } from "@/components/Button";
import { Seo } from "@/components/Seo";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

export default function CatalogPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubCategories, setSelectedSubCategories] = useState<number[]>(
    []
  );
  const [sidebarData, setSidebarData] = useState<SidebarCategory[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(9);
  const isLg = useMediaQuery("(min-width: 1024px)");
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [modalOpened, { open: openModal, close: closeModal }] =
    useDisclosure(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const transformRef = useRef<any>(null);
  const [zoom, setZoom] = useState(1);
  const [searchParams] = useSearchParams();
  const { openForm } = useApplicationForm();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const handleOpenForm = useCallback(
    (productId: number) => {
      openForm({ productId });
      closeModal();
    },
    [openForm, closeModal]
  );

  const handleProductClick = useCallback(
    (product: Product) => {
      setSelectedProduct(product);
      setZoom(1);
      openModal();
    },
    [openModal]
  );

  const handleZoomIn = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.zoomIn();
    }
  }, []);

  const handleZoomOut = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.zoomOut();
    }
  }, []);

  const handleResetZoom = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalOpened) {
        closeModal();
      }
    };

    if (modalOpened) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [modalOpened, closeModal]);

  useEffect(() => {
    fetchSidebarData();

    // Read subCategoryIds from URL
    const subCategoryIdsParam = searchParams.get("subCategoryIds");
    if (subCategoryIdsParam) {
      const ids = subCategoryIdsParam
        .split(",")
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id));
      setSelectedSubCategories(ids);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchProducts();
  }, [selectedSubCategories, page, limit]);

  const fetchSidebarData = async () => {
    try {
      const data = await api.getSidebar();
      setSidebarData(data);
    } catch (err) {
      console.error("Failed to load sidebar data:", err);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.getProducts(
        page,
        limit,
        selectedSubCategories.length > 0 ? selectedSubCategories : undefined
      );
      setProducts(response.data);
      setTotalPages(response.pagination.totalPages);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const handleSubCategoryChange = (subCategoryIds: number[]) => {
    setSelectedSubCategories(subCategoryIds);
    setPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getSelectedSubCategoryNames = () => {
    const names: string[] = [];
    sidebarData.forEach((category) => {
      category.subCategories.forEach((subCategory) => {
        if (selectedSubCategories.includes(subCategory.id)) {
          names.push(subCategory.name);
        }
      });
    });
    return names;
  };

  const getHeaderTitle = () => {
    const selectedNames = getSelectedSubCategoryNames();

    if (selectedNames.length === 0) {
      return "Каталог";
    } else if (selectedNames.length === 1) {
      return selectedNames[0]?.toUpperCase() ?? "Каталог";
    } else {
      return `${selectedNames[0]?.toUpperCase() ?? "Каталог"} и др.`;
    }
  };

  return (
    <div className="w-full">
      <Seo
        title={`PRO MEBEL — ${getHeaderTitle()}`}
        description="Каталог мебели: выбирайте категории и фильтры, смотрите варианты и оставляйте заявку."
        canonicalPath="/catalog"
      />
      <div className="max-w-360 mx-auto px-4 lg:px-15">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">{getHeaderTitle()}</h1>
          </div>
          <div className="flex gap-2 justify-center items-center">
            <Button color="#222222" variant="outline" onClick={openDrawer} className="flex-1 rounded-full" fullWidth={false}>
              Вариация фильтров
            </Button>

            {selectedSubCategories.length > 0 && (
              <div>
                <button
                  onClick={() => handleSubCategoryChange([])}
                  className="text-[#B89671] text-sm underline"
                >
                  Очистить фильтры
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block w-74.25 shrink-0">
            <Sidebar
              selectedSubCategories={selectedSubCategories}
              onSubCategoryChange={handleSubCategoryChange}
            />
          </div>

          {/* Products Grid */}
          <div className="flex-1 pb-10">
            {/* Desktop Header Section */}
            <div className="hidden lg:flex items-center justify-between mb-8">
              <h1 className="text-4xl font-bold">{getHeaderTitle()}</h1>
              <div className="flex gap-4 hidden">
                <Button
                  color="#222222"
                  variant="outline"
                  className="rounded-full border-2 border-black"
                >
                  Смотреть все товары
                </Button>
                <Button
                  color="#222222"
                  variant="outline"
                  className="rounded-full border-2 border-black py-2.5 px-7.5"
                  rightSection={<IconChevronDown size={18} />}
                >
                  Вариация фильтров
                </Button>
              </div>
            </div>
            {loading && page === 1 ? (
              <div className="flex justify-center items-center py-20">
                <Loader size="lg" />
              </div>
            ) : error ? (
              <div className="text-red-500 text-center py-20">{error}</div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                Товары не найдены
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                  {products.map((product) => {
                    // Handle both relative paths (uploads/xxx) and absolute URLs (https://...)
                    const imageUrl = product.image.startsWith("http")
                      ? product.image
                      : `${process.env.API_BASE_URL}/${product.image}`;

                    return (
                      <div
                        key={product.id}
                        className="h-112 w-full relative overflow-hidden group rounded-[30px] cursor-pointer"
                        onClick={() => handleProductClick(product)}
                      >
                        {/* Use img tag instead of background-image for better performance */}
                        <img
                          src={imageUrl}
                          alt={product.name}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-110 duration-500"
                        />
                        {/* Simplified blur effect for mobile, full effect for desktop */}
                        {isLg && (
                          <div
                            className="absolute scale-110 inset-0 transition-opacity blur-xl opacity-80"
                            style={{
                              maskImage: `linear-gradient(to top, white 0%, white 35%, transparent 50%)`,
                              WebkitMaskImage: `linear-gradient(to top, black 0%, black 25%, transparent 50%)`,
                              backgroundImage: `url(${imageUrl})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                        )}
                        <div
                          className={`absolute bottom-0 transition-all group-hover:bottom-0 p-3.75 text-white flex flex-col gap-2 bg-linear-to-t from-black/70 to-transparent pointer-events-none`}
                        >
                          <span className="text-[28px] leading-[1] font-medium">{product.name}</span>
                          <span className="text-sm leading-[120%]">
                            {product.description}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <Pagination
                      value={page}
                      onChange={handlePageChange}
                      total={totalPages}
                      size="md"
                      color="#222222"
                      withEdges
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Mobile Filter Drawer */}
        <Drawer
          opened={drawerOpened}
          onClose={closeDrawer}
          position="bottom"
          size="100%"
          title="Вариация фильтров"
          classNames={{
            title: "text-xl font-bold",
            header: "border-b pb-4",
            body: "pt-4",
            root: "z-99999999 absolute",
            inner: "z-99999999",
          }}
        >
          <div className="pt-4">
            <Sidebar
              selectedSubCategories={selectedSubCategories}
              onSubCategoryChange={(ids) => {
                handleSubCategoryChange(ids);
                closeDrawer();
              }}
            />
          </div>
        </Drawer>

        {/* Fullscreen Image Modal with Zoom */}
        <Modal
          opened={modalOpened}
          onClose={closeModal}
          fullScreen
          padding={0}
          withCloseButton={false}
          classNames={{
            content: "!bg-black/95 !shadow-none",
            body: "!p-0 !h-full",
            inner: "!z-[9999]",
            overlay: "!z-[9998]",
          }}
        >
          {selectedProduct && (
            <div className="relative w-full h-full bg-gradient-to-br from-black via-gray-900 to-black">
              {/* Controls */}
              <div className="absolute top-4 right-4 z-50 flex gap-2 items-center flex-wrap justify-end">
                <div className="flex gap-2 items-center bg-black/40 backdrop-blur-lg rounded-full px-2 py-1.5 border border-white/10 shadow-xl">
                  <button
                    onClick={handleZoomOut}
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/15 text-white rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
                    aria-label="Уменьшить"
                    disabled={zoom <= 0.5}
                  >
                    <IconZoomOut size={18} stroke={2.5} />
                  </button>
                  <span className="px-3 py-1 text-white text-xs font-semibold min-w-[50px] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={handleZoomIn}
                    className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/15 text-white rounded-full transition-all disabled:opacity-30 disabled:cursor-not-allowed hover:scale-110 active:scale-95"
                    aria-label="Увеличить"
                    disabled={zoom >= 5}
                  >
                    <IconZoomIn size={18} stroke={2.5} />
                  </button>
                  {zoom !== 1 && (
                    <button
                      onClick={handleResetZoom}
                      className="w-9 h-9 flex items-center justify-center bg-white/5 hover:bg-white/15 text-white rounded-full transition-all hover:scale-110 active:scale-95 ml-1"
                      aria-label="Сбросить масштаб"
                    >
                      <IconArrowsMaximize size={18} stroke={2.5} />
                    </button>
                  )}
                </div>
                <button
                  onClick={closeModal}
                  className="w-9 h-9 flex items-center justify-center bg-red-500/20 hover:bg-red-500/40 backdrop-blur-lg text-white rounded-full transition-all border border-red-500/30 hover:border-red-500/50 shadow-xl hover:scale-110 active:scale-95"
                  aria-label="Закрыть"
                >
                  <IconX size={18} stroke={2.5} />
                </button>
              </div>

              {/* Image Container with Zoom/Pan */}
              <TransformWrapper
                ref={transformRef}
                initialScale={1}
                minScale={0.5}
                maxScale={5}
                wheel={{ step: 0.1 }}
                panning={{ disabled: !isMobile }}
                doubleClick={{ disabled: true }}
                centerOnInit
                limitToBounds={false}
                centerZoomedOut={true}
                onZoom={(ref) => {
                  const scale = (ref as any).state?.scale || 1;
                  setZoom(scale);
                }}
                onTransformed={(ref) => {
                  const scale = (ref as any).state?.scale || 1;
                  setZoom(scale);
                }}
              >
                {({ zoomIn, zoomOut, resetTransform }) => {
                  // Store functions for external controls
                  if (transformRef.current) {
                    (transformRef.current as any).zoomIn = zoomIn;
                    (transformRef.current as any).zoomOut = zoomOut;
                    (transformRef.current as any).resetTransform = resetTransform;
                  }

                  return (
                    <TransformComponent
                      wrapperStyle={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      contentStyle={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={
                          selectedProduct.image.startsWith("http")
                            ? selectedProduct.image
                            : `${process.env.API_BASE_URL}/${selectedProduct.image}`
                        }
                        alt={selectedProduct.name}
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                          userSelect: "none",
                          WebkitUserSelect: "none",
                        }}
                        draggable={false}
                      />
                    </TransformComponent>
                  );
                }}
              </TransformWrapper>

              {/* Product Info Overlay - Only visible when not zoomed */}
              {zoom === 1 && (
                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6 bg-gradient-to-t from-black via-black/95 to-transparent pointer-events-none z-40">
                  <div className="pointer-events-auto max-w-2xl mx-auto">
                    <h3 className="text-white text-2xl lg:text-4xl font-semibold mb-3 drop-shadow-lg">
                      {selectedProduct.name}
                    </h3>
                    <p className="text-white/90 text-sm lg:text-lg mb-4 lg:mb-6 leading-relaxed drop-shadow-md">
                      {selectedProduct.description}
                    </p>
                    <Button
                      variant="white"
                      onClick={() => handleOpenForm(selectedProduct.id)}
                      className="shadow-xl hover:shadow-2xl transition-all hover:scale-105"
                    >
                      <span className="text-main font-semibold">Отправить заявку</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
      <Footer />
    </div>
  );
}
